import React from 'react';
import { Tag } from 'antd';
import type { Task, TaskLink, TaskPriority } from '../../api/tasks/interface';
import {
  Assignee,
  AssistChip,
  BlockedChip,
  DueChip,
  LinkChip,
  OwlChip,
  PriorityToggle,
  SnoozeControl,
  StreamCountChip,
  TagChips,
  TaskCheck,
  isDone,
} from './TaskBits';
import styles from './TasksTab.module.scss';

interface Props {
  task: Task;
  onToggle: () => void;
  onChangePriority: (p: TaskPriority) => void;
  onSnooze: (due: string) => void;
  onOpenLink?: (link: TaskLink) => void;
  onOpenDetail?: (taskId: string) => void;
  blockedBy?: Task[];
  activeTags?: string[];
  onTag?: (tag: string) => void;
}

const PRIORITY_CARD_CLASS: Record<TaskPriority, string> = {
  high: styles.cardHigh,
  med: styles.cardMed,
  low: styles.cardLow,
};

const TaskCard: React.FC<Props> = ({
  task,
  onToggle,
  onChangePriority,
  onSnooze,
  onOpenLink,
  onOpenDetail,
  blockedBy = [],
  activeTags,
  onTag,
}) => {
  const done = isDone(task);
  return (
    <div
      className={`${styles.card} ${PRIORITY_CARD_CLASS[task.priority]} ${
        done ? styles.done : ''
      }`}
      role={onOpenDetail ? 'button' : undefined}
      tabIndex={onOpenDetail ? 0 : undefined}
      onClick={onOpenDetail ? () => onOpenDetail(task.id) : undefined}
      style={onOpenDetail ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles.cardTop}>
        <TaskCheck done={done} onToggle={onToggle} />
        <div className={`${styles.cardTitle} ${done ? styles.doneTitle : ''}`}>
          {task.title}
        </div>
        <Assignee name={task.assignee} />
      </div>
      <div className={styles.cardFoot}>
        {task.raised_by === 'Owl' ? <OwlChip /> : null}
        {!done ? (
          <PriorityToggle
            priority={task.priority}
            onChange={onChangePriority}
          />
        ) : null}
        {!done ? <DueChip due={task.due} /> : null}
        {done ? <Tag color="default">Done</Tag> : null}
        <StreamCountChip task={task} />
        {task.kind === 'action' ? <AssistChip /> : null}
        {!done ? <BlockedChip blockers={blockedBy} /> : null}
        {task.link ? <LinkChip link={task.link} onOpen={onOpenLink} /> : null}
        {!done ? <SnoozeControl onSnooze={onSnooze} /> : null}
        <TagChips tags={task.tags} active={activeTags} onTag={onTag} />
      </div>
    </div>
  );
};

export default TaskCard;
